import { useState, useEffect, useRef } from "react";

/** Same lockup as navbar (`public/qovarix-logo-NAVBER.png`). Literal avoids stale HMR symbol bugs. */
const LOADING_LOGO_SRC = "/qovarix-logo-NAVBER.png";

const STAGES = [
  "BOOTING CORE SYSTEMS",
  "LOADING GEOMETRY",
  "INITIALIZING SIGNAL CHAMBER",
  "CALIBRATING LIQUIDITY RING",
  "ACTIVATING PARTICLE FIELD",
  "ENGINE READY",
];

const CANDLES = [
  { green: true, body: 36, topW: 12, botW: 8 },
  { green: true, body: 48, topW: 18, botW: 10 },
  { green: false, body: 28, topW: 8, botW: 16 },
  { green: true, body: 44, topW: 14, botW: 6 },
  { green: false, body: 32, topW: 6, botW: 18 },
  { green: true, body: 52, topW: 10, botW: 8 },
  { green: false, body: 24, topW: 16, botW: 10 },
  { green: true, body: 58, topW: 20, botW: 8 },
  { green: false, body: 38, topW: 8, botW: 14 },
  { green: true, body: 46, topW: 12, botW: 10 },
  { green: true, body: 42, topW: 16, botW: 6 },
  { green: false, body: 30, topW: 14, botW: 10 },
];

export default function LoadingScreen({ progress, visible }) {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  const [tick, setTick] = useState(0);
  const stageIdx = Math.min(Math.floor(displayProgress / 18), STAGES.length - 1);

  useEffect(() => {
    const id = setInterval(() => {
      setDisplayProgress((prev) => {
        const diff = progress - prev;
        if (Math.abs(diff) < 0.3) return progress;
        return prev + diff * 0.12;
      });
    }, 16);
    return () => clearInterval(id);
  }, [progress]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 50);
    return () => clearInterval(id);
  }, []);

  const prevVisible = useRef(visible);
  useEffect(() => {
    if (prevVisible.current && !visible) setFadeOut(true);
    prevVisible.current = visible;
  }, [visible]);

  if (!visible && !fadeOut) return null;

  const litCount = Math.floor((displayProgress / 100) * CANDLES.length);
  const pulse = 0.55 + 0.45 * Math.sin(tick * 0.28);

  return (
    <div
      data-testid="loading-screen"
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center"
      style={{
        background: "#030406",
        opacity: fadeOut ? 0 : 1,
        transition: "opacity 0.8s ease-out",
        pointerEvents: fadeOut ? "none" : "auto",
      }}
      onTransitionEnd={() => { if (fadeOut) setFadeOut(false); }}
    >
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 340,
          height: 340,
          background: "radial-gradient(circle, rgba(0,240,255,0.07) 0%, transparent 70%)",
          filter: "blur(50px)",
        }}
      />

      <img
        src={LOADING_LOGO_SRC}
        alt="Qovarix Loading"
        width={380}
        height={100}
        className="relative z-10 mb-8 w-[272px] sm:w-[332px] md:w-[372px] h-auto max-h-[72px] sm:max-h-20 object-contain animate-pulse"
        style={{ filter: "drop-shadow(0 0 22px rgba(0,240,255,0.32))" }}
      />

      <p
        className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00F0FF]/50 mb-6 relative z-10"
        data-testid="loading-stage"
      >
        {STAGES[stageIdx]}
      </p>

      <div className="relative z-10 flex flex-col items-center">
        <div className="flex gap-[7px]" style={{ height: 90, alignItems: "flex-end" }}>
          {CANDLES.map((c, i) => {
            const lit = i < litCount;
            const isActive = i === litCount;
            const visible_ = lit || isActive;

            const bodyColor = visible_
              ? (c.green ? "#22c55e" : "#ef4444")
              : "rgba(255,255,255,0.07)";
            const wickColor = visible_
              ? (c.green ? "#16a34a" : "#dc2626")
              : "rgba(255,255,255,0.04)";
            const glow = visible_
              ? (c.green ? "0 0 7px rgba(34,197,94,0.45)" : "0 0 7px rgba(239,68,68,0.45)")
              : "none";
            const opacity = isActive ? pulse : (lit ? 1 : 0.45);

            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  height: 90,
                  opacity,
                  transition: "opacity 0.25s ease",
                }}
              >
                <div style={{ width: 1.5, height: c.topW, background: wickColor, borderRadius: 1 }} />
                <div
                  style={{
                    width: 11,
                    height: c.body,
                    background: bodyColor,
                    borderRadius: 2,
                    boxShadow: glow,
                  }}
                />
                <div style={{ width: 1.5, height: c.botW, background: wickColor, borderRadius: 1 }} />
              </div>
            );
          })}
        </div>

        <div
          style={{
            width: "100%",
            height: 1,
            background: "rgba(0,240,255,0.08)",
            marginTop: 5,
          }}
        />

        <div className="flex justify-end w-full mt-2">
          <span className="font-mono text-[10px] text-white/30" data-testid="loading-percent">
            {Math.round(displayProgress)}%
          </span>
        </div>
      </div>

      <div className="absolute top-6 left-6 w-5 h-5 border-l border-t border-[#00F0FF]/15" />
      <div className="absolute top-6 right-6 w-5 h-5 border-r border-t border-[#00F0FF]/15" />
      <div className="absolute bottom-6 left-6 w-5 h-5 border-l border-b border-[#00F0FF]/15" />
      <div className="absolute bottom-6 right-6 w-5 h-5 border-r border-b border-[#00F0FF]/15" />
    </div>
  );
}
